import debug from "debug";
import TextureLoader from "./TextureLoader";
const log = debug("app:makegeom");


/**
 * Manages the caching and loading of textures using the `TextureLoader` class, including KTX2, SVG, and fallback textures.
 * @category Experience
 * @subcategory Utils
 */
class TextureVault {
  static instance;

  /**
   * Initializes the TextureVault singleton with the given experience context.
   * @param {Object} experience - The experience object containing renderer and configuration details.
   */
  constructor(experience) {
    if (TextureVault.instance) {
      return TextureVault.instance;
    }
    TextureVault.instance = this;

    this.experience = experience;
    this.eventHub = this.experience.eventHub;
    this.textures = new Map(); // In-memory cache for textures

    this.textureLoader = new TextureLoader(this.experience.renderer);
  }

  cleanup() {
    console.log("Cleaning up TextureVault: Disposing textures and releasing resources.");

    // Dispose of all cached textures
    this.textures.forEach((texture, key) => {
        if (texture instanceof THREE.Texture) {
            texture.dispose();
        }
    });

    this.textures.clear(); // Clear the texture cache
    this.textureLoader.cleanup(); // Cleanup the TextureLoader resources

    console.log("TextureVault cleanup complete.");
}

  /**
   * Retrieves the singleton instance of TextureVault.
   * @param {Object} experience - The experience object containing renderer and configuration details.
   * @returns {TextureVault} The singleton instance of TextureVault.
   */
  static getInstance(experience) {
    if (!TextureVault.instance) {
      TextureVault.instance = new TextureVault(experience);
    }
    return TextureVault.instance;
  }

  /**
   * Checks if a texture exists at the given URL.
   * @param {string} url - The URL of the texture to check.
   * @returns {Promise<boolean>} A promise resolving to `true` if the texture exists, otherwise `false`.
   */
  async fetchTexture(url) {
    try {
      const response = await fetch(url);
      return response.ok;
    } catch {
      return false;
    }
  }

  /**
   * Retrieves a texture by its key, trying KTX2, SVG, and fallback textures in that order.
   * Caches the loaded texture for future use.
   * @param {string} key - The key or identifier of the texture.
   * @returns {Promise<THREE.Texture>} A promise resolving to the loaded texture.
   * @throws {Error} If all attempts to load the texture fail.
   */
  async getTexture(key) {
    if (this.textures.has(key)) {
      return this.textures.get(key);
    }
    log("thisiskey", key);

    // First, try to load the KTX2 texture
    const fullUrlKTX2 = `${this.experience.config.assetURL}${key.replace(
      ".svg",
      ".ktx2"
    )}`;
    const ktx2Exists = await this.fetchTexture(fullUrlKTX2);

    if (ktx2Exists) {
      try {
        const texture = await this.textureLoader.loadTexture({
          type: "ktx2",
          key: key.replace(".svg", ".ktx2"),
          url: fullUrlKTX2,
        });
        this.textures.set(key, texture.texture);
        return texture.texture;
      } catch {
        log(`Failed to load KTX2 texture for key: ${key}`);
      }
    }

    // If KTX2 fails, try to load the SVG texture
    const fullUrlSVG = `${this.experience.config.assetURL}${key}`;
    const svgExists = await this.fetchTexture(fullUrlSVG);

    if (svgExists) {
      try {
        const texture = await this.textureLoader.loadTexture({
          type: "svg",
          key,
          url: fullUrlSVG,
        });
        this.textures.set(key, texture.texture);
        return texture.texture;
      } catch {
        log(`Failed to load SVG texture for key: ${key}`);
      }
    }

    // Fallback texture if both KTX2 and SVG fail
    try {
      const texture = await this.textureLoader.loadTexture({
        type: "fallback",
        key,
        url: null,
      });
      this.textures.set(key, texture.texture);
      return texture.texture;
    } catch (error) {
      console.error(`Failed to load fallback texture for key ${key}: ${error}`);
      throw error;
    }
  }

  /**
   * Retrieves multiple textures by their keys.
   * @param {string[]} keys - An array of texture keys.
   * @returns {Promise<THREE.Texture[]>} A promise resolving to an array of loaded textures.
   * @throws {Error} If any texture fails to load.
   */
  async getTextures(keys) {
    try {
      log("keys", keys);
      return await Promise.all(keys.map((key) => this.getTexture(key)));
    } catch (error) {
      console.error(`Error loading textures:`, error);
      throw error;
    }
  }
}

export default TextureVault;
